import { useCallback, useMemo } from 'react';
import { createGoogleClient } from '../utils';

interface UseGoogleAddressSearchConfig {
  countryRestriction?: string;
  baseUrl?: string;
}

interface GetGoogleAddressOptions {
  input: string;
}

interface GoogleAddressItem {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GoogleGeocodeResult {
  formatted_address: string;
  address_components: AddressComponent[];
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  types: string[];
}

interface MappedAddress {
  name: string;
  state: string;
  cityName: string;
  id: string;
  street: string;
  location: {
    latitude: number;
    longitude: number;
  };
  title: string;
  zipCode: string;
  administrativeArea: string;
  locationType: string;
  formattedAddress: string;
}

const mapAddress = (address: GoogleGeocodeResult): MappedAddress => {
  const addressData: any = {};

  addressData.locationType = address.types?.[0]?.split('_').join(' ') || 'unknown';
  addressData.formattedAddress = address.formatted_address.replace(/,[^,]*$/, '');

  address.address_components.forEach((item) => {
    if (item.types.includes('postal_code')) {
      addressData.zipCode = item.long_name;
    } else if (item.types.includes('neighborhood')) {
      addressData.cityName = item.long_name;
    } else if (item.types.includes('locality')) {
      addressData.cityName = item.long_name;
    } else if (item.types.includes('street_number')) {
      addressData.street = item.long_name;
    } else if (item.types.includes('premise')) {
      addressData.building = item.long_name;
    } else if (item.types.includes('administrative_area_level_1')) {
      addressData.administrativeArea = item.short_name;
      addressData.state = item.long_name;
    }
  });

  const { locationType, formattedAddress, zipCode, cityName, street, administrativeArea, state } =
    addressData;

  const cityId = `${cityName}--${administrativeArea}`.replace(/\s+/g, '_');

  return {
    name: formattedAddress,
    state,
    cityName,
    id: cityId,
    street,
    location: {
      latitude: address?.geometry?.location?.lat,
      longitude: address?.geometry?.location?.lng,
    },
    title: formattedAddress,
    zipCode,
    administrativeArea,
    locationType,
    formattedAddress,
  };
};

const useGoogleAddressSearch = (config: UseGoogleAddressSearchConfig) => {
  const { countryRestriction = 'US', baseUrl = '' } = config;

  const googleClient = useMemo(() => createGoogleClient(baseUrl || ''), [baseUrl]);

  const getGoogleAddresses = useCallback(
    async ({ input }: GetGoogleAddressOptions): Promise<MappedAddress[]> => {
      if (input?.length < 3) return [];

      try {
        const response = await googleClient.get('/geocode', {
          params: {
            address: input,
            country: countryRestriction,
          },
        });

        const addresses = Array.isArray(response.data)
          ? response.data.map((el: any) => mapAddress(el))
          : [];

        return addresses;
      } catch (error) {
        console.error('Address search error:', error);
        throw error;
      }
    },
    [googleClient, countryRestriction],
  );

  return { getGoogleAddresses };
};

export default useGoogleAddressSearch;
export type {
  UseGoogleAddressSearchConfig,
  GetGoogleAddressOptions,
  GoogleAddressItem,
  MappedAddress,
};
